import { log, logError } from '../../utils/logging';

/**
 * Wraps paragraph OOXML into the minimal package format required by Word's insertOoxml method.
 * 
 * The insertOoxml method typically expects the same format as getOoxml returns - a full package
 * with namespaces and structure. This utility wraps a paragraph OOXML fragment into that format.
 * 
 * Based on Microsoft Office Open XML format:
 * https://learn.microsoft.com/en-us/office/open-xml/word-processing
 */
export class OoxmlPackageWrapper {
  /**
   * Wraps a paragraph OOXML fragment into a minimal package format for insertOoxml
   * @param paragraphOoxml - The paragraph OOXML (w:p element and contents)
   * @returns Package OOXML suitable for insertOoxml, or empty string if wrapping fails
   */
  static wrapParagraphInPackage(paragraphOoxml: string): string {
    if (!paragraphOoxml || paragraphOoxml.trim().length === 0) {
      logError('[OoxmlPackageWrapper] Empty paragraph OOXML provided');
      return '';
    }

    try {
      // Defensive normalization: this wrapper embeds its input inside <w:document><w:body>...
      // If the caller accidentally passes a <w:body>...</w:body> or <w:document>...</w:document>
      // wrapper, the result has nested <w:body> elements which is invalid OOXML and is rejected
      // by Word Win32 `insertOoxml` with GeneralException. Strip outer wrappers so we always
      // embed paragraph-level content.
      const normalizedParagraphOoxml =
        OoxmlPackageWrapper.unwrapToParagraphContent(paragraphOoxml);

      log('[OoxmlPackageWrapper] Wrapping paragraph in package format');
      log(`[OoxmlPackageWrapper] Paragraph OOXML length: ${normalizedParagraphOoxml.length}`);
      log(
        `[OoxmlPackageWrapper] Paragraph OOXML preview: ${normalizedParagraphOoxml.substring(0, 200)}`,
      );

      // Create the minimal OOXML package structure
      // This follows the format returned by getOoxml() - a pkg:package with parts
      const packageXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml">
        <w:body>
          ${normalizedParagraphOoxml}
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;

      log('[OoxmlPackageWrapper] Package created successfully');
      log(`[OoxmlPackageWrapper] Package length: ${packageXml.length}`);
      
      return packageXml;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Error wrapping paragraph in package', error);
      return '';
    }
  }

  /**
   * True when getOoxml() captured table markup (`w:tbl` / `w:tc`).
   *
   * A table-cell paragraph's getOoxml() often returns the whole table. Feeding that package back
   * through Range.insertOoxml(..., replace) nests or splits the table instead of restoring the cell.
   */
  static containsTableMarkup(xml: string | null | undefined): boolean {
    if (!xml) return false;
    return /<(?:\w+:)?(?:tbl|tc)(?:\s|\/|>)/.test(xml);
  }

  /**
   * Strip outer <w:document>/<w:body> wrappers if present, returning paragraph-level content
   * (one or more <w:p> siblings, with any <w:sectPr> stripped to avoid altering section props).
   *
   * Why: callers occasionally pass the result of `paragraph.getOoxml()` extraction that on
   * Win32 includes a <w:body> wrapper with surrounding section context. Embedding such input
   * directly inside <w:body> produces invalid nested <w:body><w:body> OOXML that Win32
   * `insertOoxml` rejects with GeneralException.
   */
  private static unwrapToParagraphContent(input: string): string {
    const trimmed = input.trim();
    if (!trimmed) return trimmed;

    // Fast path: already a single <w:p> (or its self-closing variant).
    if (/^<w:p[\s>/]/i.test(trimmed)) return trimmed;

    try {
      const parser = new DOMParser();
      // Use a permissive root to avoid namespace prefix issues when parsing fragments.
      const wrapped = `<root xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml" xmlns:w16du="http://schemas.microsoft.com/office/word/2023/wordml/word16du" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">${trimmed}</root>`;
      const doc = parser.parseFromString(wrapped, 'text/xml');
      const parseError = doc.getElementsByTagName('parsererror')[0];
      if (parseError) return trimmed;

      const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
      const pNodes = doc.getElementsByTagNameNS(W_NS, 'p');
      if (pNodes.length === 0) return trimmed;

      const serializer = new XMLSerializer();
      let out = '';
      for (let i = 0; i < pNodes.length; i++) {
        out += serializer.serializeToString(pNodes.item(i)!);
      }
      return out || trimmed;
    } catch {
      return trimmed;
    }
  }

  /**
   * Return a getOoxml() package with the body trimmed to contain exactly the target paragraph.
   *
   * Use this when you need to round-trip Word's own paragraph.getOoxml() result back into
   * Range.insertOoxml(...) to faithfully restore that paragraph (all original styles,
   * tracked changes, formatting, run properties, etc.) without manually reconstructing OOXML.
   *
   * Why this is needed:
   *   - On Word Mac, `paragraph.getOoxml()` typically returns a package whose body contains a
   *     single <w:p>. Inserting that package back via Range.insertOoxml works directly.
   *   - On Word Win32, `paragraph.getOoxml()` can return a package whose body contains the
   *     target paragraph PLUS trailing context (siblings, <w:sectPr>). Inserting that package
   *     unchanged would duplicate or shift the surrounding paragraphs.
   *   - A hand-crafted minimal package (see `wrapParagraphInPackage`) is rejected by Word Win32
   *     `insertOoxml` with `GeneralException` for many real-world paragraphs because the
   *     hand-crafted wrapper lacks namespace declarations / package parts that Win32 requires.
   *
   * Strategy:
   *   - Parse the package, find <w:body>, keep ONLY the target <w:p>, drop all sibling <w:p>
   *     and <w:sectPr> elements. Preserve every other aspect of Word's original output
   *     (namespaces, package parts, content types, attribute order).
   *   - This is round-trip-safe: Word reliably re-ingests its own output.
   *
   * @param packageXml The pkg:package XML returned by `paragraph.getOoxml()`
   * @param targetParagraphText Plain text of the target paragraph (used to disambiguate when
   *   the body contains multiple <w:p>). Optional but strongly recommended on Win32.
   * @returns A pkg:package XML containing only the target paragraph in <w:body>, or empty
   *   string if parsing failed.
   */
  static keepOnlyTargetParagraphInPackage(
    packageXml: string,
    targetParagraphText?: string | null,
  ): string {
    if (!packageXml?.trim()) {
      logError('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: empty package');
      return '';
    }

    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();
    const target =
      targetParagraphText != null && targetParagraphText.length > 0
        ? normalize(targetParagraphText)
        : null;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(packageXml, 'text/xml');
      const parserError = doc.getElementsByTagName('parsererror')[0];
      if (parserError) {
        logError(
          '[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: parse error',
          parserError.textContent,
        );
        return '';
      }

      const bodyNodes = doc.getElementsByTagNameNS(W_NS, 'body');
      if (bodyNodes.length === 0) {
        // Some packages may not declare the namespace on the root; try local-name scan.
        const fallback = Array.from(doc.getElementsByTagName('*')).find(
          (el) => (el.localName || el.tagName.split(':').pop()) === 'body',
        );
        if (!fallback) {
          log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: no <w:body> found');
          return '';
        }
        return OoxmlPackageWrapper.pruneBodyToTargetParagraph(doc, fallback, target);
      }
      return OoxmlPackageWrapper.pruneBodyToTargetParagraph(doc, bodyNodes.item(0)!, target);
    } catch (error) {
      logError('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage error', error);
      return '';
    }
  }

  private static pruneBodyToTargetParagraph(
    doc: Document,
    body: Element,
    targetNormalizedText: string | null,
  ): string {
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();

    const childParagraphs: Element[] = [];
    const childSectPrs: Element[] = [];
    const otherChildren: Element[] = [];

    for (let i = 0; i < body.childNodes.length; i++) {
      const node = body.childNodes.item(i);
      if (node.nodeType !== 1) continue;
      const el = node as Element;
      const localName = el.localName || el.tagName.split(':').pop();
      if (el.namespaceURI === W_NS || !el.namespaceURI) {
        if (localName === 'p') childParagraphs.push(el);
        else if (localName === 'sectPr') childSectPrs.push(el);
        else otherChildren.push(el);
      } else {
        otherChildren.push(el);
      }
    }

    if (childParagraphs.length === 0) {
      log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: no <w:p> in body');
      return '';
    }

    let chosen: Element | null = null;
    if (targetNormalizedText && targetNormalizedText.length > 0) {
      for (const p of childParagraphs) {
        if (normalize(p.textContent || '') === targetNormalizedText) {
          chosen = p;
          break;
        }
      }
    }
    if (!chosen) {
      for (const p of childParagraphs) {
        if (normalize(p.textContent || '').length > 0) {
          chosen = p;
          break;
        }
      }
    }
    if (!chosen) chosen = childParagraphs[0];

    for (const p of childParagraphs) {
      if (p !== chosen) body.removeChild(p);
    }
    for (const sectPr of childSectPrs) {
      body.removeChild(sectPr);
    }

    const serializer = new XMLSerializer();
    const out = serializer.serializeToString(doc);
    log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: pruned body', {
      originalParagraphCount: childParagraphs.length,
      droppedSectPrCount: childSectPrs.length,
      droppedOtherChildren: otherChildren.length,
      outputLen: out.length,
    });
    return out;
  }

  /**
   * Cell `getOoxml()` usually returns the whole `w:tbl`. Undo must restore the pre-apply
   * paragraph (including `w:ins` / `w:del`) without feeding that table back through insertOoxml.
   *
   * Lift the matching `<w:p>` into the existing package's `<w:body>` via string replace so Word's
   * pkg namespaces/parts survive (DOMSerializer rewrites often make Win32 insertOoxml reject).
   */
  static extractCellParagraphPackage(
    packageXml: string,
    targetParagraphText?: string | null,
  ): string {
    if (!packageXml?.trim()) return '';
    if (!OoxmlPackageWrapper.containsTableMarkup(packageXml)) {
      return OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(packageXml, targetParagraphText);
    }

    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();
    const xmlPlainText = (fragment: string): string =>
      normalize(
        fragment
          .replace(/<[^>]+>/g, '')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'"),
      );
    const hasTrackChanges = (fragment: string): boolean =>
      /<(?:\w+:)?(?:ins|del)(?:\s|\/|>)/.test(fragment);

    try {
      const paraRe = /<(\w+:)?p(?![A-Za-z])(?:\s[^>]*)?(?:\/>|>[\s\S]*?<\/\1p>)/g;
      const paragraphs: string[] = [];
      let match: RegExpExecArray | null;
      while ((match = paraRe.exec(packageXml)) !== null) {
        paragraphs.push(match[0]);
      }
      if (paragraphs.length === 0) return '';

      const target =
        targetParagraphText != null && targetParagraphText.length > 0
          ? normalize(targetParagraphText)
          : null;

      let chosen: string | null = null;
      let bestScore = -1;
      for (const p of paragraphs) {
        const text = xmlPlainText(p);
        let score = 0;
        if (target) {
          if (text === target) score += 100;
          else if (text.includes(target)) score += 50;
          else if (target.includes(text) && text.length > 20) score += 30;
        }
        if (hasTrackChanges(p)) score += 10;
        if (text.length > 0) score += 1;
        if (score > bestScore) {
          bestScore = score;
          chosen = p;
        }
      }
      if (!chosen) chosen = paragraphs[0];

      const bodyMatch = packageXml.match(/<(\w+:)?body\b[^>]*>[\s\S]*?<\/\1body>/);
      if (!bodyMatch) {
        return OoxmlPackageWrapper.wrapParagraphInPackage(chosen);
      }
      const openTag = bodyMatch[0].match(/^<[^>]+>/)?.[0];
      if (!openTag) return '';
      const prefix = bodyMatch[1] || '';
      const out = packageXml.replace(bodyMatch[0], `${openTag}${chosen}</${prefix}body>`);
      if (OoxmlPackageWrapper.containsTableMarkup(out)) return '';
      log('[OoxmlPackageWrapper] extractCellParagraphPackage: lifted cell w:p', {
        paragraphCount: paragraphs.length,
        outputLen: out.length,
        score: bestScore,
      });
      return out;
    } catch (error) {
      logError('[OoxmlPackageWrapper] extractCellParagraphPackage error', error);
      return '';
    }
  }

  /**
   * Package that is safe to insertOoxml-replace into a paragraph (body or table cell).
   * Table snapshots are lifted to a single `w:p` first; table markup is never returned.
   */
  static toInsertableParagraphPackage(
    packageXml: string | null | undefined,
    targetParagraphText?: string | null,
  ): string {
    if (!packageXml?.trim()) return '';
    const lifted = OoxmlPackageWrapper.containsTableMarkup(packageXml)
      ? OoxmlPackageWrapper.extractCellParagraphPackage(packageXml, targetParagraphText)
      : packageXml;
    if (!lifted || OoxmlPackageWrapper.containsTableMarkup(lifted)) return '';
    return lifted;
  }

  /**
   * Replace the target w:p inside <w:body> of a getOoxml() package.
   * Preserves Word's original package shape (namespaces, parts) — insertOoxml expects this format.
   *
   * Counts/replaces only direct <w:p> children of <w:body>, not <w:p> in styles or other parts
   * (a global regex match falsely reported 7+ paragraphs and fell back to wrapParagraphInPackage).
   */
  static replaceParagraphInPackage(originalPackageXml: string, newParagraphOoxml: string): string {
    const para = (newParagraphOoxml || '').trim();
    if (!originalPackageXml?.trim() || !para) {
      logError('[OoxmlPackageWrapper] replaceParagraphInPackage: missing input');
      return '';
    }

    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(originalPackageXml, 'text/xml');
      const parserError = doc.getElementsByTagName('parsererror')[0];
      if (parserError) {
        log('[OoxmlPackageWrapper] replaceParagraphInPackage: parse error, falling back to wrap');
        return this.wrapParagraphInPackage(para);
      }

      let body: Element | null = null;
      const bodyNodes = doc.getElementsByTagNameNS(W_NS, 'body');
      if (bodyNodes.length > 0) {
        body = bodyNodes.item(0);
      } else {
        body =
          Array.from(doc.getElementsByTagName('*')).find(
            (el) => (el.localName || el.tagName.split(':').pop()) === 'body',
          ) ?? null;
      }
      if (!body) {
        log('[OoxmlPackageWrapper] replaceParagraphInPackage: no w:body, falling back to wrap');
        return this.wrapParagraphInPackage(para);
      }

      const bodyParagraphs: Element[] = [];
      for (let i = 0; i < body.childNodes.length; i++) {
        const node = body.childNodes.item(i);
        if (node.nodeType !== 1) continue;
        const el = node as Element;
        const localName = el.localName || el.tagName.split(':').pop();
        if (localName === 'p' && (el.namespaceURI === W_NS || !el.namespaceURI)) {
          bodyParagraphs.push(el);
        }
      }

      if (bodyParagraphs.length === 0) {
        log('[OoxmlPackageWrapper] replaceParagraphInPackage: no w:p in body, falling back to wrap');
        return this.wrapParagraphInPackage(para);
      }

      const wrappedPara = `<root xmlns:w="${W_NS}">${para}</root>`;
      const paraDoc = parser.parseFromString(wrappedPara, 'text/xml');
      const paraParseError = paraDoc.getElementsByTagName('parsererror')[0];
      if (paraParseError) {
        log('[OoxmlPackageWrapper] replaceParagraphInPackage: new paragraph parse error');
        return this.wrapParagraphInPackage(para);
      }

      const newParagraphEl = paraDoc.getElementsByTagNameNS(W_NS, 'p').item(0);
      if (!newParagraphEl) {
        log('[OoxmlPackageWrapper] replaceParagraphInPackage: no w:p in new paragraph XML');
        return this.wrapParagraphInPackage(para);
      }

      const imported = doc.importNode(newParagraphEl, true);
      body.replaceChild(imported, bodyParagraphs[0]);
      for (let i = 1; i < bodyParagraphs.length; i++) {
        if (bodyParagraphs[i].parentNode === body) {
          body.removeChild(bodyParagraphs[i]);
        }
      }

      const out = new XMLSerializer().serializeToString(doc);
      log('[OoxmlPackageWrapper] replaceParagraphInPackage: replaced w:p in live package body', {
        bodyParagraphCount: bodyParagraphs.length,
      });
      return out;
    } catch (error) {
      logError('[OoxmlPackageWrapper] replaceParagraphInPackage error, falling back to wrap', error);
      return this.wrapParagraphInPackage(para);
    }
  }

  /**
   * Validates that the wrapped package is well-formed XML
   * @param packageXml - The package XML to validate
   * @returns true if valid, false otherwise
   */
  static validatePackage(packageXml: string): boolean {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(packageXml, 'text/xml');
      
      // Check for parsing errors
      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        logError('[OoxmlPackageWrapper] Package validation failed - parse error', parserError.textContent);
        return false;
      }
      
      // Check for required structure
      const packageElement = doc.querySelector('package, pkg\\:package');
      if (!packageElement) {
        logError('[OoxmlPackageWrapper] Package validation failed - no package element found');
        return false;
      }
      
      log('[OoxmlPackageWrapper] Package validation passed');
      return true;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Package validation failed with error', error);
      return false;
    }
  }
}
